import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { Step, StepsData, VisibilityKey } from "@/content/schema";
import {
	DEFAULT_VIEW,
	easeOutCubic,
	FRAMING_SAMPLES,
	framePose,
	verticalFov,
} from "./framing";
import { focusNames } from "./labels";
import { clipTime, frameAtFraction } from "./playback";
import { visibleAt } from "./visibility";

const HFOV_DEG = 43.6; // 45 mm lens on a 36 mm sensor, like the Blender camera
/** Sage accent of the theme (tokens.css --accent, light) as the emissive tint of a hovered part. */
const HIGHLIGHT_COLOR = 0x45714f;
/** Fallback floor colour when the CSS token cannot be read (tokens.css --scene-floor). */
const FLOOR_COLOR = 0x7f7c71;

/** The floor colour of the current theme: a mid-tone white and dark furniture both read on. */
function floorColor(): THREE.Color {
	const value = getComputedStyle(document.documentElement)
		.getPropertyValue("--scene-floor")
		.trim();
	return value ? new THREE.Color(value) : new THREE.Color(FLOOR_COLOR);
}

function computeBounds(geometry: THREE.BufferGeometry): THREE.Box3 {
	geometry.computeBoundingBox();
	return geometry.boundingBox ?? new THREE.Box3();
}

interface HighlightEntry {
	mesh: THREE.Mesh;
	original: THREE.Material | THREE.Material[];
	material: THREE.MeshStandardMaterial;
	strong: boolean;
}

export interface ViewerOptions {
	/** Lower shadow resolution and pixel ratio (phones). */
	lite?: boolean;
}

/** Layer of the meshes hidden at the current frame (the camera only renders layer 0). */
const HIDDEN_LAYER = 1;

/**
 * Meshes that draw a glTF node itself, without those of its child nodes. `steps.json` visibility is
 * Blender's `hide_render`, which hides an object and not its children, whereas an invisible three.js
 * object hides its whole subtree: visibility is therefore applied to these meshes with layers (not
 * inherited), never with `Object3D.visible`.
 */
export function ownMeshes(
	object: THREE.Object3D,
	isNode: (o: THREE.Object3D) => boolean,
): THREE.Mesh[] {
	const meshes: THREE.Mesh[] = object instanceof THREE.Mesh ? [object] : [];
	// a node with several materials is a group of primitive meshes next to its child nodes
	for (const child of object.children)
		if (child instanceof THREE.Mesh && !isNode(child)) meshes.push(child);
	return meshes;
}

/**
 * Imperative three.js engine: scene, model, frame-driven animation, per-step framing and highlighting.
 * It knows nothing about React; the UI drives it through `setFrame`, `frameStep`, `highlight`.
 */
export class Viewer {
	readonly renderer: THREE.WebGLRenderer;
	readonly scene = new THREE.Scene();
	readonly camera: THREE.PerspectiveCamera;
	readonly controls: OrbitControls;

	private root: THREE.Group | null = null;
	private mixer: THREE.AnimationMixer | null = null;
	private action: THREE.AnimationAction | null = null;
	private data: StepsData | null = null;
	private visibility: { meshes: THREE.Mesh[]; keys: VisibilityKey[] }[] = [];
	private highlights = new Map<string, HighlightEntry>();
	private frame = 1;
	private themeObserver: MutationObserver | null = null;
	private scheme: MediaQueryList | null = null;
	private onScheme: (() => void) | null = null;
	private pulseTime = 0;
	private camFrom = new THREE.Vector3();
	private camTo = new THREE.Vector3();
	private tgtFrom = new THREE.Vector3();
	private tgtTo = new THREE.Vector3();
	private camT = 1;
	private disposed = false;

	constructor(
		canvas: HTMLCanvasElement,
		private readonly container: HTMLElement,
		options: ViewerOptions = {},
	) {
		this.renderer = new THREE.WebGLRenderer({
			canvas,
			antialias: true,
			alpha: true,
		});
		this.renderer.setPixelRatio(
			Math.min(window.devicePixelRatio, options.lite ? 1.5 : 2),
		);
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 1.05;
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;
		this.renderer.shadowMap.enabled = true;
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

		const pmrem = new THREE.PMREMGenerator(this.renderer);
		this.scene.environment = pmrem.fromScene(
			new RoomEnvironment(),
			0.04,
		).texture;
		this.scene.environmentIntensity = 0.9;
		pmrem.dispose();

		this.camera = new THREE.PerspectiveCamera(30, 1, 0.02, 60);
		this.camera.position.set(2.4, 1.45, 2.3);
		this.controls = new OrbitControls(this.camera, canvas);
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.08;
		this.controls.maxPolarAngle = Math.PI * 0.52;
		this.controls.minDistance = 0.15;
		this.controls.maxDistance = 8;
		this.controls.target.set(0.7, 0.3, 0);

		const shadowSize = options.lite ? 1024 : 2048;
		const sun = new THREE.DirectionalLight(0xfff4e6, 2.6);
		sun.position.set(-1.6, 3.2, 2.4);
		sun.castShadow = true;
		sun.shadow.mapSize.set(shadowSize, shadowSize);
		sun.shadow.bias = -0.0004;
		sun.shadow.normalBias = 0.02;
		Object.assign(sun.shadow.camera, {
			left: -2.5,
			right: 2.5,
			top: 2.5,
			bottom: -2.5,
			near: 0.5,
			far: 10,
		});
		this.scene.add(sun, new THREE.HemisphereLight(0xdfe6f0, 0x8c8578, 0.35));

		const floor = new THREE.Mesh(
			new THREE.CircleGeometry(6, 64),
			new THREE.MeshStandardMaterial({ color: floorColor(), roughness: 0.95 }),
		);
		// the floor follows the theme (data-theme on <html>, or the system scheme)
		this.themeObserver = new MutationObserver(() => {
			floor.material.color.copy(floorColor());
		});
		this.themeObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["data-theme"],
		});
		this.scheme = window.matchMedia("(prefers-color-scheme: dark)");
		this.onScheme = () => floor.material.color.copy(floorColor());
		this.scheme.addEventListener("change", this.onScheme);
		floor.rotation.x = -Math.PI / 2;
		floor.position.y = -0.0005;
		floor.receiveShadow = true;
		this.scene.add(floor);
		this.resize();
	}

	/** Loads the glTF model and binds the steps data. `onProgress` receives 0..1. */
	async load(
		modelUrl: string,
		data: StepsData,
		onProgress?: (p: number) => void,
	): Promise<void> {
		this.unloadModel();
		const gltf = await new GLTFLoader().loadAsync(modelUrl, (e) => {
			if (e.total) onProgress?.(e.loaded / e.total);
		});
		if (this.disposed) return;
		this.data = data;
		const root = gltf.scene;
		this.root = root;
		this.scene.add(root);
		root.traverse((o) => {
			if (o instanceof THREE.Mesh) {
				o.castShadow = o.name !== "Mur";
				o.receiveShadow = true;
				const mat = o.material as THREE.MeshStandardMaterial;
				if (mat.map) mat.map.anisotropy = 8;
			}
		});
		const isNode = (o: THREE.Object3D) =>
			gltf.parser.associations.get(o)?.nodes !== undefined;
		this.visibility = Object.entries(data.visibility).flatMap(
			([name, keys]) => {
				const object = root.getObjectByName(name);
				return object ? [{ meshes: ownMeshes(object, isNode), keys }] : [];
			},
		);
		const clip = gltf.animations[0];
		if (!clip) throw new Error("Le modèle ne contient pas d’animation");
		this.mixer = new THREE.AnimationMixer(this.root);
		this.action = this.mixer.clipAction(clip);
		this.action.play();
		this.action.paused = true;
		this.setFrame(1);
	}

	/** Puts every part exactly where it is at `frame` (animation + visibility). */
	setFrame(frame: number): void {
		if (!this.mixer || !this.action || !this.data) return;
		this.frame = frame;
		// `mixer.setTime` is a no-op on a paused action: set the action time and evaluate with a zero delta.
		this.action.time = clipTime(frame, this.data.product.fps);
		this.mixer.update(0);
		for (const v of this.visibility) {
			const layer = visibleAt(v.keys, frame) ? 0 : HIDDEN_LAYER;
			for (const mesh of v.meshes) mesh.layers.set(layer);
		}
	}

	get currentFrame(): number {
		return this.frame;
	}

	/** Bounding sphere of the step's moving parts, sampled across the gesture (visible meshes only). */
	private stepBoundingSphere(root: THREE.Group, step: Step): THREE.Sphere {
		const box = new THREE.Box3();
		const part = new THREE.Box3();
		const names = focusNames(step);
		const keep = this.frame;
		for (const t of FRAMING_SAMPLES) {
			this.setFrame(Math.round(frameAtFraction(step, t)));
			root.updateMatrixWorld(true);
			for (const name of names) {
				const object = root.getObjectByName(name);
				if (!object) continue;
				object.traverse((m) => {
					if (!(m instanceof THREE.Mesh)) return;
					if (!m.layers.test(this.camera.layers)) return;
					const bounds = m.geometry.boundingBox ?? computeBounds(m.geometry);
					part.copy(bounds).applyMatrix4(m.matrixWorld);
					box.union(part);
				});
			}
		}
		this.setFrame(keep);
		if (box.isEmpty()) {
			root.updateMatrixWorld(true);
			box.setFromObject(root);
		}
		const sphere = new THREE.Sphere();
		box.getBoundingSphere(sphere);
		return sphere;
	}

	/** Moves the camera (smoothly, or instantly) to the framing computed for `step`. */
	frameStep(step: Step, instant = false): void {
		if (!this.root) return;
		const sphere = this.stepBoundingSphere(this.root, step);
		const pose = framePose(
			sphere.center,
			sphere.radius,
			step.view ?? DEFAULT_VIEW,
			this.camera.fov,
			this.camera.aspect,
		);
		this.camFrom.copy(this.camera.position);
		this.tgtFrom.copy(this.controls.target);
		this.camTo.set(pose.position.x, pose.position.y, pose.position.z);
		this.tgtTo.set(pose.target.x, pose.target.y, pose.target.z);
		this.camT = instant ? 1 : 0;
		if (instant) {
			this.camera.position.copy(this.camTo);
			this.controls.target.copy(this.tgtTo);
		}
	}

	/** Highlights parts with a pulsing (or strong, on hover) blue emissive. */
	highlight(names: readonly string[], on: boolean, strong = false): void {
		if (!this.root) return;
		for (const name of names) {
			const object = this.root.getObjectByName(name);
			if (!object) continue;
			object.traverse((m) => {
				if (!(m instanceof THREE.Mesh)) return;
				const entry = this.highlights.get(m.uuid);
				if (on) {
					if (entry) entry.strong = strong;
					else {
						const material = (m.material as THREE.MeshStandardMaterial).clone();
						material.emissive = new THREE.Color(HIGHLIGHT_COLOR);
						material.emissiveIntensity = 0;
						this.highlights.set(m.uuid, {
							mesh: m,
							original: m.material,
							material,
							strong,
						});
						m.material = material;
					}
				} else if (entry) {
					if (strong)
						entry.strong = false; // hover ended: keep the pulse
					else this.removeHighlight(m.uuid);
				}
			});
		}
	}

	clearHighlights(): void {
		for (const id of [...this.highlights.keys()]) this.removeHighlight(id);
	}

	private removeHighlight(id: string): void {
		const e = this.highlights.get(id);
		if (!e) return;
		e.mesh.material = e.original;
		e.material.dispose();
		this.highlights.delete(id);
	}

	/** Per-frame update: camera transition, highlight pulse, controls, render. `pulse` false freezes the pulse off. */
	tick(dt: number, pulse: boolean): void {
		this.pulseTime += dt;
		const level = 0.18 + 0.22 * (0.5 + 0.5 * Math.sin(this.pulseTime * 4));
		for (const h of this.highlights.values())
			h.material.emissiveIntensity = h.strong ? 0.6 : pulse ? level : 0;
		if (this.camT < 1) {
			this.camT = Math.min(1, this.camT + dt / 0.75);
			const k = easeOutCubic(this.camT);
			this.camera.position.lerpVectors(this.camFrom, this.camTo, k);
			this.controls.target.lerpVectors(this.tgtFrom, this.tgtTo, k);
		}
		this.controls.update();
		this.renderer.render(this.scene, this.camera);
	}

	resize(): void {
		const w = this.container.clientWidth;
		const h = this.container.clientHeight;
		if (!w || !h) return;
		this.renderer.setSize(w, h, false);
		this.camera.aspect = w / h;
		this.camera.fov = verticalFov(HFOV_DEG, this.camera.aspect);
		this.camera.updateProjectionMatrix();
	}

	private unloadModel(): void {
		this.clearHighlights();
		if (this.root) {
			this.scene.remove(this.root);
			this.root.traverse((o) => {
				if (o instanceof THREE.Mesh) {
					o.geometry.dispose();
					const mats = Array.isArray(o.material) ? o.material : [o.material];
					for (const m of mats) {
						const std = m as THREE.MeshStandardMaterial;
						std.map?.dispose();
						m.dispose();
					}
				}
			});
		}
		this.root = null;
		this.mixer = null;
		this.action = null;
		this.visibility = [];
	}

	dispose(): void {
		this.disposed = true;
		this.themeObserver?.disconnect();
		if (this.scheme && this.onScheme)
			this.scheme.removeEventListener("change", this.onScheme);
		this.unloadModel();
		this.controls.dispose();
		this.renderer.dispose();
	}
}
